import { defineAll } from '@app/utils/defineAll'
import type { Moment } from '@app/utils/moment'
import moment from '@app/utils/moment'
import { omit } from '@app/utils/omit'

import type { AnnouncementSearchQuery } from '@app/packages/api/v2/types/params'
import { MAP_AREA_KIND } from '@app/packages/geo/constants'
import { createUnwrappedThunkSelector, unwrapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'

import { announcementsSearchAddressResultSelector } from '@app/store/selectors/announcement'
import { profilePlaceTimezoneResultSelector } from '@app/store/slices/profile_place'
import { createThunk } from '@app/store/thunk'

import type { AnnouncementsFilter } from './AnnouncementsFilter'

export function getDefaultFilter() {
  return createThunk((dispatch): AnnouncementsFilter => {
    const defaultAddress = dispatch(unwrapThunkSelector(announcementsSearchAddressResultSelector, 'Announcements search address is not loaded yet'))
    return { order_by: 'start_time', order_dir: 'asc', created_by: 'except_ignored', address: defaultAddress }
  })
}

export function getCleanFilter(filter: AnnouncementsFilter, now?: Moment) {
  return createThunk(dispatch => {
    const defaultFilter = dispatch(getDefaultFilter())

    for (const key in filter) {
      if (Object.prototype.hasOwnProperty.call(filter, key)) {
        const k = key as keyof AnnouncementsFilter
        if (filter[k] === defaultFilter[k]) {
          filter = omit(filter, k)
        }
      }
    }

    if (now) {
      {
        const mdate = toMoment(filter.min_date)
        if (mdate && mdate.isBefore(now, 'day')) {
          filter = omit(filter, 'min_date')
        }
      }

      {
        const mdate = toMoment(filter.max_date)
        if (mdate && mdate.isBefore(now, 'day')) {
          filter = omit(filter, 'max_date')
        }
      }
    }
    return filter
  })
}

export function getEffectiveFilter(filter: AnnouncementsFilter, now?: Moment) {
  return createThunk(dispatch => {
    return { ...dispatch(getDefaultFilter()), ...dispatch(getCleanFilter(filter, now)) }
  })
}

export function convertFilterToQuery({ page, per_page, filter }: { page: number; per_page?: number; filter: AnnouncementsFilter }) {
  return createThunk((dispatch, getState): AnnouncementSearchQuery => {
    const timezone = createUnwrappedThunkSelector(profilePlaceTimezoneResultSelector, 'Profile place timezone is not loaded yet')(getState())
    filter = dispatch(getEffectiveFilter(filter))
    const address = filter.address

    return defineAll<AnnouncementSearchQuery>({
      online: !!filter.online,
      require_attention: filter.require_attention,
      date: undefined,
      min_date: filter.min_date,
      max_date: filter.max_date,
      min_hours: filter.min_hours,
      max_hours: filter.max_hours,
      start: filter.start,
      end: filter.end,
      min_kids: filter.min_kids,
      max_kids: filter.max_kids,
      youngest_age_months: filter.youngest_age_months,
      eldest_age_months: filter.eldest_age_months,
      regular: filter.regular,

      latitude: !filter.online && address ? address.location.lat : undefined,
      longitude: !filter.online && address ? address.location.lon : undefined,

      'bbox[bl][lat]': !filter.online && address?.kind === MAP_AREA_KIND && address.bounds ? address.bounds.bottomLeft.lat : undefined,
      'bbox[bl][lon]': !filter.online && address?.kind === MAP_AREA_KIND && address.bounds ? address.bounds.bottomLeft.lon : undefined,
      'bbox[tr][lat]': !filter.online && address?.kind === MAP_AREA_KIND && address.bounds ? address.bounds.topRight.lat : undefined,
      'bbox[tr][lon]': !filter.online && address?.kind === MAP_AREA_KIND && address.bounds ? address.bounds.topRight.lon : undefined,
      distance: undefined,

      order_by: filter.order_by,
      order_dir: filter.order_dir,
      timezone,

      created_after: undefined,
      created_by: filter.created_by,
      page,
      per_page,
    })
  })
}

const toMoment = (value: unknown) => {
  if (typeof value !== 'string' || !value) return undefined
  return moment(value, 'YYYY-MM-DD')
}
